import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      id: 1,
      question: "What are your pickup and delivery timings?",
      answer: `We offer pickup and delivery services from 7:00 AM to 8:00 PM, Monday through Saturday. Sunday pickups are available from 8:00 AM to 6:00 PM.\n\nFor same-day service, please schedule your pickup before 10:00 AM. We also offer express 4-hour service for urgent requirements with additional charges.`
    },
    {
      id: 2,
      question: "How do I schedule a pickup?",
      answer: `You can schedule a pickup through multiple convenient methods:\n\n• Call us at +91 96000 44004\n• WhatsApp us at +91 96000 44004\n• Fill out our online contact form\n• Visit our facility directly\n\nWe recommend booking at least 2 hours in advance for regular service and 24 hours for weekend pickups.`
    },
    {
      id: 3,
      question: "What areas do you cover in Coimbatore?",
      answer: `Our primary service areas include:\n\n• Avinashi Road and surrounding areas\n• Brookefields Mall vicinity\n• Peelamedu and nearby localities\n• Gandhipuram Central\n\nWe also provide extended coverage to RS Puram, Saibaba Colony, Vadavalli, and Singanallur with additional charges or weekend-only service. Contact us to check availability for your specific location.`
    },
    {
      id: 4,
      question: "What services do you offer?",
      answer: `We provide comprehensive laundry solutions:\n\n• Regular Wash & Fold\n• Premium Dry Cleaning\n• Professional Ironing & Pressing\n• Stain Removal Treatment\n• Fabric Care & Restoration\n• Express Same-Day Service\n• Bulk Laundry for Businesses\n\nAll services include free pickup and delivery within our primary service areas.`
    },
    {
      id: 5,
      question: "How much do your services cost?",
      answer: `Our pricing is competitive and transparent:\n\n• Regular Wash: ₹15-25 per piece\n• Dry Cleaning: ₹50-150 per piece\n• Ironing: ₹10-20 per piece\n• Express Service: 50% additional charge\n\nWe offer package deals for regular customers and bulk orders. Contact us for detailed pricing based on your specific requirements.`
    },
    {
      id: 6,
      question: "Do you handle delicate fabrics and special garments?",
      answer: `Yes, we specialize in handling delicate and premium fabrics:\n\n• Silk, wool, and cashmere garments\n• Designer clothing and formal wear\n• Wedding dresses and traditional attire\n• Leather and suede items\n• Curtains and home textiles\n\nOur experienced team uses appropriate cleaning methods and premium care products for each fabric type.`
    },
    {
      id: 7,
      question: "What if I'm not satisfied with the service?",
      answer: `Customer satisfaction is our top priority. We offer:\n\n• 100% satisfaction guarantee\n• Free re-cleaning if not satisfied\n• Damage protection policy\n• Quick resolution of any issues\n\nIf you're not completely satisfied, contact us within 24 hours of delivery and we'll make it right at no additional cost.`
    },
    {
      id: 8,
      question: "Do you offer emergency or urgent services?",
      answer: `Yes, we understand urgent laundry needs:\n\n• 4-hour express service available\n• Same-day delivery for orders before 10 AM\n• 24/7 emergency contact: +91 96000 44004\n• Weekend and holiday service available\n\nEmergency services carry additional charges but ensure your garments are ready when you need them most.`
    }
  ];

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
      className="bg-card rounded-2xl p-8 shadow-elevation-2"
    >
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-foreground mb-4">
          Frequently Asked Questions
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          Find quick answers to common questions about our laundry services, pickup schedules, and policies.
        </p>
      </div>
      <div className="space-y-4">
        {faqs?.map((faq, index) => (
          <motion.div
            key={faq?.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 * index }}
            className="border border-border rounded-xl overflow-hidden"
          >
            <button
              onClick={() => toggleFAQ(faq?.id)}
              className="w-full px-6 py-4 text-left flex items-center justify-between bg-muted/20 hover:bg-muted/40 transition-smooth"
            >
              <span className="font-medium text-foreground pr-4">
                {faq?.question}
              </span>
              <motion.div
                animate={{ rotate: openFAQ === faq?.id ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                className="flex-shrink-0"
              >
                <Icon name="ChevronDown" size={20} color="var(--color-muted-foreground)" />
              </motion.div>
            </button>

            <AnimatePresence>
              {openFAQ === faq?.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 py-4 bg-card">
                    <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                      {faq?.answer}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
      {/* Still Have Questions */}
      <div className="mt-8 pt-6 border-t border-border text-center">
        <h3 className="text-lg font-semibold text-foreground mb-3">
          Still Have Questions?
        </h3>
        <p className="text-muted-foreground mb-4">
          Can't find what you're looking for? Our customer service team is here to help.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => window.open('tel:+919600044004')}
            className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium transition-smooth hover:hover-scale hover:shadow-elevation-2"
          >
            <Icon name="Phone" size={18} className="mr-2" />
            Call Customer Service
          </button>
          
          <button
            onClick={() => window.open('https://wa.me/919600044004')}
            className="inline-flex items-center justify-center px-6 py-3 bg-success text-success-foreground rounded-lg font-medium transition-smooth hover:hover-scale hover:shadow-elevation-2"
          >
            <Icon name="MessageCircle" size={18} className="mr-2" />
            WhatsApp Us
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default FAQSection;